import { ISession } from '../types';
import urls, { IS_DEPLOYED_ENV } from '../urls';
import extendSessionProperties from '../util/extendSessionProperties';
import SessionManager from '../util/SessionManager';
import {
  buildVanityUrl,
  getAccountIdFromUrl,
  getHostEnv,
  stringifyParams,
} from './url';

const IS_PRODUCTION = getHostEnv() !== 'dev';

const LOCAL_STORAGE_KEY = 'jupiterone-auth';

interface LoginQueryParams {
  /**
   * The `apps-auth` service uses the `app` querystring parameter for
   * redirect purposes after login.
   */
  app: string;
  /**
   * destination is provided as something
   * for client side handling, on load
   * the webpage is supposed to handle the destination
   * parameter on login to handle deep linking.
   * We currently do not have any logic in place
   * to handle this therefore it is optional and
   * not used at all in this file.
   */
  destination?: string;
  subdomain?: string;
}

interface LogoutQueryParams extends LoginQueryParams {
  /**
   * The `apps-auth` service's logout route requires `redirect_uri`
   * to be specified, otherwise it errors.
   */
  redirect_uri: string;
}

export default function createSessionManager(client: any) {
  const sessionManager = new SessionManager({
    storageKey: LOCAL_STORAGE_KEY,

    getLocation() {
      return window.location;
    },

    getOauthBaseUri() {
      return urls.oauthBaseUri;
    },

    getAuthRedirectUri() {
      return urls.appRedirect;
    },

    getTokenUrl() {
      return urls.token;
    },

    getAccountsUrl() {
      return urls.accounts;
    },

    getVanityAccountId() {
      return getAccountIdFromUrl();
    },

    getLoginUrl() {
      const appPath = window.location.pathname.substr(1);
      const destination = window.location.search;
      const queryParams: LoginQueryParams = {
        app: appPath,
        destination,
      };

      let loginUrl = urls.loginDeprecated;
      const accountId = getAccountIdFromUrl();

      // Attach the accountId from the vanity URL to the subdomain query params
      // so that the user can use SSO
      if (accountId) {
        queryParams.subdomain = accountId;
      }

      loginUrl += `?${stringifyParams(queryParams)}`;

      if (!IS_PRODUCTION) {
        const redirectDomain = `${window.location.protocol}//${window.location.host}`;
        loginUrl += `&redirectDomain=${redirectDomain}`;
      }

      return loginUrl;
    },

    getLogoutUrl: () => {
      const app = window.location.pathname.substr(1);
      // we don't need to specify the current app we're on here
      // as the redirect uri because the query params
      // specify our current app / path which will
      // be returned as the redirect uri after
      // re-authentication (re-login)
      const queryParams: LogoutQueryParams = {
        app,
        redirect_uri: urls.appRedirect,
      };

      let logoutUrl = urls.logout;
      const accountId = getAccountIdFromUrl();
      if (accountId) {
        queryParams.subdomain = accountId;
      }

      logoutUrl += `?${stringifyParams(queryParams)}`;

      if (!IS_PRODUCTION) {
        const redirectDomain = `${window.location.protocol}//${window.location.host}`;
        logoutUrl += `&redirectDomain=${redirectDomain}`;
      }
      return logoutUrl;
    },

    setItem(k: string, v: any) {
      const session = extendSessionProperties(v);
      window.localStorage.setItem(k, JSON.stringify(session));
    },

    getItem(k: string) {
      const item = window.localStorage.getItem(k);

      try {
        return item && JSON.parse(item);
      } catch (err) {
        console.error(
          'Error fetching auth from local storage. Logging out.',
          err
        );
        sessionManager.logout();
      }
    },

    removeItem(k: string) {
      window.localStorage.removeItem(k);
    },

    onUpdateSession(session: ISession) {
      if (client) client.session = session; // Support legacy implementations of the session provider
      if (sessionManager._client) sessionManager._client.session = session;
    },

    onLogout(redirectLocation: string) {
      window.location.href = redirectLocation;
    },

    onAccountSet(accountId: string, subdomain?: string) {
      // In a non-localhost environment, we should redirect the user to a vanity
      // URL containing their accountId so that they can go through their SSO
      // provider.
      if (IS_DEPLOYED_ENV) {
        window.location.href = buildVanityUrl({ accountId, subdomain });
      } else {
        window.location.reload();
      }
    },

    onError(err: Error | string) {
      // TODO: Show a UI error to the client
      console.error('Session error: ', err);
      sessionManager.logout();
    },
  });

  return sessionManager;
}
